import { useCallback } from "react";
import { Button, H2, Divider, Box, Flex, Icon } from "orcs-design-system";
import { useLocation, matchPath } from "react-router-dom";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import {
  useActiveFilterCount,
  useClearFilters,
  useFilters,
} from "src/contexts/filterAndFte/FilterAndFteContext";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackEvent } from "src/services/segment";
import useGuidedTourTrigger from "src/guidedTour/useGuidedTourTrigger";
import useTourForRouteMatch from "src/guidedTour/useTourForRouteMatch";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import PATHS from "src/consts/urlPaths";
import { getInsightsFilterCount } from "src/util/allocationStatus";
import { useSidebarContext, ACTIONS } from "../../context";

import {
  ALLOCATION_STATUS_OPTIONS,
  FILTER_CONFIGS,
  buildOptions,
} from "./filterConfigs";
import type { FilterConfig, OptionLabelType } from "./filterConfigs";
import FilterSelect from "./FilterSelect";

const CustomWrapper = styled.div`
  [class$="Group"] {
    padding: 0;
    border-bottom: solid 1px ${themeGet("colors.surfaceRaisedBorder")};
    &:last-of-type {
      border-bottom: none;
    }
  }
  [class$="group"] {
    margin: 0;
    padding: 0;
  }
  [class$="option"] {
    padding: 6px 6px 6px 12px;
  }
  [class^="Tag__TagValue"] {
    padding: 2px;
    &:hover {
      background-color: transparent !important;
      border-color: transparent !important;
    }
  }
  [class^="Tag__TagType"] {
    margin-left: 2px;
  }
`;

type FilterSidebarContentProps = {
  hideHeading?: boolean;
  /** Storybook composites only: render filter slots without Select chrome. */
  storybookFilterStubs?: boolean;
  /** Storybook composites only: fixed filter row list (skips route / flag logic). */
  storybookFilterConfigs?: FilterConfig[];
};

const FilterSidebarContent = ({
  hideHeading = false,
  storybookFilterStubs = false,
  storybookFilterConfigs,
}: FilterSidebarContentProps) => {
  const location = useLocation();
  const { peopleTagTypesFilter } = useWorkspaceFeatureFlags();
  const isSummaryPage = !!matchPath(location.pathname, PATHS.INSIGHTS);
  const activeFilterCount = useActiveFilterCount();
  const filters = useFilters();
  const clearFilters = useClearFilters();
  const triggerGuidedTour = useGuidedTourTrigger();
  const tourName = useTourForRouteMatch();
  const queryParams = new URLSearchParams(location.search);
  const showGuidedTourButton = !queryParams.has("guided_tour") && isSummaryPage;
  const displayedActiveFilterCount = isSummaryPage
    ? getInsightsFilterCount(activeFilterCount, filters)
    : activeFilterCount;

  const [{ filterItems, query }, dispatch] = useSidebarContext();

  const isFilterLoading = useCallback(
    (filterKey: string) => {
      return query.loading && query.variables.searchTermsKey === filterKey;
    },
    [query]
  );
  const onInputChange = useCallback(
    (key: string, value: string) => {
      dispatch({ type: ACTIONS.FILTER_ITEM_USER_SEARCH, key, value });
      trackEvent(EVENT_TRACKING.SETTING_CHANGED, {
        setting: "summary_filter_changed",
        value: key,
      });
    },
    [dispatch]
  );

  const filterConfigs: FilterConfig[] = storybookFilterConfigs ?? [
    FILTER_CONFIGS.COMPANY,
    FILTER_CONFIGS.COUNTRY,
    FILTER_CONFIGS.LOCATION,
    FILTER_CONFIGS.JOBS,
    FILTER_CONFIGS.TEAM_TYPES,
    FILTER_CONFIGS.TEAMS,
  ];

  if (!storybookFilterConfigs) {
    if (!isSummaryPage) {
      filterConfigs.push(FILTER_CONFIGS.ALLOCATION_STATUS);
    }

    if (peopleTagTypesFilter === true) {
      filterConfigs.splice(4, 0, FILTER_CONFIGS.PERSON_TAGS_AND_TAG_TYPES);
    }
  }
  return (
    <div className="guided-tour-filter-sidebar-content">
      {!hideHeading && (
        <Flex alignItems="center">
          <H2 fontSize="1.2rem" fontWeight="bold" mr="r">
            Filter by
          </H2>

          {showGuidedTourButton && (
            <Button
              small
              variant="successAlternate"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                triggerGuidedTour(tourName);
              }}
            >
              <Icon icon={["fas", "play-circle"]} />
              Guided Tour
            </Button>
          )}
        </Flex>
      )}
      <Box my="r">
        <Divider light />
        <Box mt="r">
          {filterConfigs.map((filterConfig) => {
            const remoteSearchProps = {
              onInputChange: (
                value: string,
                {
                  action,
                  prevInputValue,
                }: { action: string; prevInputValue: string }
              ) => {
                if (action === "input-change" && prevInputValue !== value) {
                  onInputChange(filterConfig.name, value);
                } else if (action === "menu-close") {
                  onInputChange("", "");
                }
              },
              isSearching: isFilterLoading(filterConfig.name),
            };

            const options =
              filterConfig.name === "allocationStatus"
                ? ALLOCATION_STATUS_OPTIONS
                : buildOptions(filterItems, filterConfig, filters);

            return (
              <CustomWrapper key={filterConfig.name}>
                {storybookFilterStubs ? (
                  <Box
                    aria-hidden
                    data-testid={`filter-stub-${filterConfig.name}`}
                    minHeight="2.5rem"
                    py="s"
                  />
                ) : (
                  <>
                    {/* TODO: this reads `filterConfig.formatOptionLabel`, but
                        filter configs set `optionLabelType` instead - so this
                        always falls back to "selectOption". Pre-existing
                        behavior, left unchanged. */}
                    <FilterSelect
                      optionLabelType={
                        (filterConfig.formatOptionLabel as
                          | OptionLabelType
                          | undefined) || "selectOption"
                      }
                      className={`guided-tour-filter-${filterConfig.name
                        .toLowerCase()
                        .replace(/\./g, "-")}`}
                      options={options}
                      {...(filterConfig.isRemoteSearch
                        ? remoteSearchProps
                        : {})}
                      {...filterConfig}
                    />
                  </>
                )}
              </CustomWrapper>
            );
          })}

          <Button
            small
            className="guided-tour-filter-clear-btn"
            onClick={() => {
              clearFilters();
              trackEvent(EVENT_TRACKING.SETTING_CHANGED, {
                setting: "summary_filter_clear_all",
                value: true,
              });
            }}
            variant={displayedActiveFilterCount !== 0 ? "default" : "disabled"}
          >
            Clear All
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default FilterSidebarContent;
